import { Component, Prop, State, h, Host, Element, Event, EventEmitter, Watch, Method } from '@stencil/core';

@Component({
  tag: 'dda-select',
  // styleUrls: ['../../global/input.css', '../../global/global.css'],
  shadow: false,
})
export class Ddaselect {
  @Element() el: HTMLElement;

  @Prop() label: string;
  @Prop() options: string;
  @Prop() selected: string; // can be id or text
  @Prop() disabled: boolean = false;
  @Prop() error?: string;
  @Prop() error_message: string;
  @Prop() helper_text: string;
  @Prop() validation_type?: string;
  @Prop() validation_id: string = 'text';
  @Prop() size?: string;
  @Prop() custom_class?: string = '';
  @Prop() component_mode?: string;
  @Prop() button_id: string;
  @Prop() toggle_button_name: string;
  @Prop() option_select_button_name: string;
  @Prop() placeholder: string = '';
  @Prop() main_aria_label: string = '';
  @Prop() input_name: string = '';

  @State() is_open: boolean = false;
  @State() selectedId: number;
  @State() selectedText: string;

  @Event() selectChanged: EventEmitter<{ id: number; text: string }>;
  @Event() onChanged: EventEmitter<any>;

  @Event() selectBlurred: EventEmitter<{ name: string; value: string }>;
  @Event() onBlurred: EventEmitter<any>;

  private get parsedOptions(): { id: number; text: string }[] {
    try {
      return JSON.parse(this.options);
    } catch {
      return [];
    }
  }

  componentWillLoad() {
    const selectedOption = this.parsedOptions.find(opt => opt.id.toString() === this.selected || opt.text === this.selected);
    if (selectedOption) {
      this.selectedId = selectedOption.id;
      this.selectedText = selectedOption.text;
    }
    if (this.selected) {
      this.watchSelected(this.selected);
    }
  }

  toggleSelect() {
    if (!this.disabled) {
      this.is_open = !this.is_open;
    }
  }

  selectOption(option: { id: any; text: string }) {
    if (!this.disabled) {
      this.selectedId = option.id;
      this.selectedText = option.text;
      this.is_open = false;
      this.selectChanged.emit(option);
      this.onChanged.emit(option.id);

      this.el.dispatchEvent(
        new InputEvent('input', {
          bubbles: true,
          composed: true,
          data: option.id,
          inputType: 'select',
        }),
      );
    }
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.is_open && !this.el.contains(event.target as Node)) {
      this.is_open = false;
    }
  };

  handleBlur = () => {
    this.selectBlurred.emit({
      name: this.input_name || this.button_id,
      value: this.selectedText || '',
    });
    this.onBlurred.emit(this.selectedId);

    this.el.dispatchEvent(
      new FocusEvent('blur', {
        bubbles: true,
        composed: true,
      }),
    );
  };

  connectedCallback() {
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  @Method()
  async setSelected(value: string | number) {
    const selectedOption = this.parsedOptions.find(opt => opt.id.toString() === value.toString() || opt.text === value);

    if (selectedOption) {
      this.selectedId = selectedOption.id;
      this.selectedText = selectedOption.text;
      this.selected = selectedOption.id.toString();
    } else {
      this.selectedId = undefined;
      this.selectedText = '';
      this.selected = '';
    }
  }

  async setValue(value: string | number) {
    const selectedOption = this.parsedOptions.find(opt => opt.id.toString() === value.toString() || opt.text === value);

    if (selectedOption) {
      this.selectedId = selectedOption.id;
      this.selectedText = selectedOption.text;
      this.selected = selectedOption.id.toString();
    } else {
      this.selectedId = undefined;
      this.selectedText = '';
      this.selected = '';
    }
  }

  @Method()
  async getSelectedText() {
    return this.selectedText;
  }

  async getValue() {
    return this.selectedText || '';
  }

  @Watch('selected')
  watchSelected(newVal: string) {
    const selectedOption = this.parsedOptions.find(opt => opt.id.toString() === newVal || opt.text === newVal);

    if (selectedOption) {
      this.selectedId = selectedOption.id;
      this.selectedText = selectedOption.text;
    } else {
      this.selectedId = undefined;
      this.selectedText = '';
    }
  }

  private numberToWord(num: number): string {
    const words = ['One', 'Two', 'Three', 'Four', 'Five'];
    return words[num - 1] || num.toString();
  }

  render() {
    const labelId = `${this.button_id}-label`;
    const valueId = `${this.button_id}-value`;

    return (
      <Host>
        <div
          class={`
            dda-input-container ${this.custom_class} 
            ${this.component_mode} 
            ${this.disabled ? 'dda-input-disabled' : ''} 
            dda-input-size-${this.size} dda-validation-${this.validation_type}
          `}
        >
          {this.label && (
            <span id={labelId} class="dda-input-label">
              {this.label}
            </span>
          )}
          <div class="dda-dropdown-container">
            <button
              name={this.toggle_button_name}
              id={this.button_id}
              type="button"
              class="dda-input-field dda-select-header"
              aria-labelledby={`${labelId} ${this.button_id}`}
              disabled={this.disabled}
              onClick={() => this.toggleSelect()}
              onBlur={this.handleBlur}
            >
              <span id={valueId} class="dda-select-button-label">
                {this.selectedText || this.placeholder}
              </span>
              <i class="material-icons" aria-hidden="true">
                {this.is_open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              </i>
            </button>

            {this.is_open && (
              <div class="dda-input-dropdown-list dda-select-list">
                {this.parsedOptions.length > 0 ? (
                  this.parsedOptions.map((option, index) => (
                    <button
                      name={`option${this.numberToWord(index + 1)}`}
                      type="button"
                      class={`dda-input-dropdown-item ${this.selectedId === option.id ? 'selected' : ''}`}
                      role="option"
                      aria-selected={this.selectedId === option.id ? 'true' : 'false'}
                      onClick={() => this.selectOption(option)}
                    >
                      {option.text}
                    </button>
                  ))
                ) : (
                  <div class="dda-input-dropdown-item">No options available</div>
                )}
              </div>
            )}
          </div>

          {this.validation_type === 'error' && this.error_message ? (
            <span class="dda-error-message" id={this.validation_id} role="alert">
              {this.error_message}
            </span>
          ) : (
            this.helper_text && (
              <span class="dda-helper-text" id={this.validation_id}>
                {this.helper_text}
              </span>
            )
          )}
        </div>
      </Host>
    );
  }
}
